import {
  Box,
  Button,
  createStyles,
  Dialog,
  DialogContent,
  Divider,
  makeStyles,
  TextField,
  Theme,
  withStyles
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {onCallRenameClipAPI, getStimulusAnalysis, initAnalysisData} from "../../../store/actions/creator/analysis";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import {apiClient} from "../../../config/apiClient";

const useStyles = makeStyles(() => ({
  root: {},
  paper: {
    maxWidth: 500,
    borderRadius: '24px!important'
  },
  btn: {
    width: 87,
    height: 37,
    borderRadius: 6,
    background: '#017AEF',
    color: '#fff',
    textTransform: 'none',
    fontSize: 14,
    '&:hover': {
      background: '#0f9fef',
      color: '#fff',
    },
  },
  cancelBtn: {
    margin: '0 15px',
    height: 37,
    borderRadius: 6,
    background: '#f2f2f2',
    textTransform: 'none',
    fontSize: 14,
    color: '#9ea6ae',
    '&:hover': {
      background: '#f2f2f2',
    },
  },
  contentText: {
    marginTop: '12px',
    fontFamily: 'roboto-regular',
  },
  experienceName: {
    marginTop: '12px',
    fontWeight: 'bold',
    fontFamily: 'roboto-regular',
  },
  label: {
    "&$focusedLabel": {
      fontSize: 12,
      fontFamily: 'roboto-bold',
      color: "#606B76"
    },
    "&$erroredLabel": {
      color: "#707070"
    }
  },
  focusedLabel: {},
  erroredLabel: {},
  underline: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    "&$error:after": {
      borderBottomColor: "#017AEF"
    },
    "&:after": {
      borderBottom: `3px solid #017AEF`
    }
  },
  error: {},
}));

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      paddingTop: 26
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      background: 'transparent!important'
    },
    header: {
      color: '#212D40',
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      paddingLeft: 17,
      paddingBottom: 7,
      display: 'flex',
      alignItems: 'center'
    }
  });

const DialogTitle = withStyles(styles)((props: any) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <div className={classes.header}>
        {children}
        {onClose ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <img src={'/assets/icons/creatordashboard/project-modal-close.svg'} alt={'img'}/>
          </IconButton>
        ) : null}
      </div>
    </MuiDialogTitle>
  );
});

interface IProps {
  issearchpage: boolean,
  selectedClip: any;
  open: boolean;
  onClose: () => void
}

const RenameClipModal = (props: IProps) => {
  const classes = useStyles();
  const { issearchpage, selectedClip, open, onClose } = props;
  const { stimulus } = useSelector((state: any) => state.analysis);
  const dispatch = useDispatch();
  const [clipName, setClipName] = useState('');
  const [isDisable, setIsDisable] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";
  
  useEffect(() => {
    if (open) {
      setClipName(selectedClip.name);
      setIsDisable(true);
    }
  }, [open]);

  const onChangeName = (event: any) => {
    setClipName(event.target.value);
    if (!event.target.value){
      setIsDisable(true);
      return
    }
    if (selectedClip.name === event.target.value) {
      setIsDisable(true);
    } else {
      setIsDisable(false);
    }
  };

  const onRenameClip = async () => {
    const request = {
      clip: {
        title: clipName
      }
    };

    const params = {
      clip_id: selectedClip.clip_id,
      session_code: selectedClip.session_code,
      session_type: selectedClip.session_type,
      tenant_id: loginUserData.customer_id,
      stimulus_id: selectedClip.session_type === 'ASYNC' && stimulus ? stimulus.stimulus_id : '',
      search: issearchpage,
    };

    if(selectedClip.session_type === 'ASYNC' && stimulus === null){
      dispatch(initAnalysisData());
      const res = await apiClient(true).get(`/stimulus/v2/stimulus/${loginUserData.customer_id}/${selectedClip.session_code}`);
      dispatch(getStimulusAnalysis(res.data && res.data[0]));
      if(res.data ){
        params.stimulus_id = res.data[0].stimulus_id;
      }
    }

    
    setModalLoading(true);
    dispatch(onCallRenameClipAPI(request, params, (res: any) => {
      if (res) {
        setTimeout(() => {
          closeModal();
          setModalLoading(false);
        }, 2000)
      } else {
        closeModal();
        setModalLoading(false);
      }
    }))
  };

  const closeModal = () => {
    setClipName('');
    onClose();
  };

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth={true}
      maxWidth={'sm'}
      scroll='paper'
      open={open}
      classes={{
        root: classes.root,
        paper: classes.paper
      }}
    >
      <DialogTitle onClose={closeModal}>
        Clip Rename
      </DialogTitle>
      <Divider/>
      <DialogContent>
        <Box>
          <div className={classes.contentText}>
            <TextField
              value={clipName}
              InputLabelProps={{
                classes: {
                  root: classes.label,
                  focused: classes.focusedLabel,
                  error: classes.erroredLabel
                },
              }}
              InputProps={{
                classes: {
                  root: classes.underline,
                  error: classes.error
                }
              }}
              fullWidth
              size="small"
              id="clipName"
              label="Clip Name"
              name="clipName"
              onChange={onChangeName}
            />
          </div>
        </Box>
      </DialogContent>

      <Box mt={3} mb={3} mr={4} display='flex' justifyContent='flex-end'>
        <Button className={classes.cancelBtn} onClick={closeModal}>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={isDisable}
          className={classes.btn}
          onClick={onRenameClip}
          style={{ backgroundColor: isDisable ? '#F0F0F0' : '#017AEF' }}
        >
          {modalLoading ? <CircularProgress color="inherit" size={20}/> : 'Update'}
        </Button>
      </Box>
    </Dialog>
  );

};

export default RenameClipModal;